'use client';

import { AlertCircle, MessageCircle, RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { useLogger } from '@/lib/client-logger';
import { useBetaFeatures } from '@/lib/hooks/api/settings-hooks';
import { isRateLimitError } from '@/lib/is-rate-limit-error';
import { readStringField } from '@/lib/utils/type-guards';
import type { Tweet as TweetType } from '../../types/tweet';
import { ConversationSummary } from './ConversationSummary';
import { ConversationThread } from './ConversationThread';
import { useConversationThread } from './useConversationThread';

interface ConversationDrawerProperties {
  isOpen: boolean;
  onClose: () => void;
  // Render function for a tweet item (passed from parent)
  renderTweet: (tweet: TweetType) => ReactNode;
  tweetId: string | null;
}

// Interface for future use in conversation thread visualization
// interface TweetWithDepth {
//   tweet: any;
//   depth: number;
//   isOriginalPoster: boolean;
//   parentId?: string;
// }

export function ConversationDrawer({ tweetId, isOpen, onClose, renderTweet }: ConversationDrawerProperties) {
  const { thread, isLoading, error, refetch, isFetching } = useConversationThread(tweetId, {
    enabled: isOpen,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
  const sheetLogger = useLogger('sheet');
  const hasLoggedOpenReference = useRef(false);
  const { user } = useAuth();
  const userRole = readStringField(user, 'role');
  const userId = readStringField(user, 'id');
  const isAdmin = userRole === 'admin';
  const { data: betaFeatures } = useBetaFeatures({ enabled: Boolean(userId) });
  const powerUserEnabled = isAdmin || Boolean(betaFeatures?.powerUser);
  const canUseAdminControls = powerUserEnabled;
  const denormalizedData = thread;
  const rateLimited = isRateLimitError(error);

  if (!isOpen) {
    hasLoggedOpenReference.current = false;
  } else if (!hasLoggedOpenReference.current) {
    hasLoggedOpenReference.current = true;
    sheetLogger.info('Tweet sheet opened', {
      tweetId: tweetId ?? null,
      hasTweetId: Boolean(tweetId),
    });
  }

  const handleReload = () => {
    refetch({ throwOnError: false }).catch((reloadError: unknown) => {
      console.error('Conversation reload failed', reloadError);
    });
  };

  const renderThreadSection = () => {
    if (!denormalizedData) {
      return null;
    }

    if (denormalizedData.tweetIds.length === 0) {
      return (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground">No tweets found in this conversation</p>
        </div>
      );
    }

    return (
      <ConversationThread
        thread={denormalizedData}
        renderTweet={renderTweet}
        highlightTweetId={tweetId ?? undefined}
        className="pb-4"
      />
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full max-w-full sm:max-w-xl lg:max-w-lg p-0 rounded-none" side="right">
        <SheetHeader className="px-4 py-3 border-b border-border/60/70 dark:border-white/10">
          <SheetTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Conversation
            {denormalizedData && (
              <span className="text-sm text-muted-foreground">
                ({denormalizedData.conversation.tweetCount}{' '}
                {denormalizedData.conversation.tweetCount === 1 ? 'tweet' : 'tweets'})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-60px)] px-3">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <CuteSpinner size={24} className="border-muted-foreground/30 border-t-muted-foreground" />
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <span>
                  {rateLimited
                    ? 'Twitter is temporarily over capacity. Please try again in a few minutes.'
                    : 'Failed to load conversation. Please try again later.'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {canUseAdminControls ? (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleReload}
                    disabled={isFetching}
                    aria-label="Reload conversation"
                  >
                    {isFetching ? (
                      <CuteSpinner size={16} className="border-current/40 border-t-current" aria-hidden="true" />
                    ) : (
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    )}
                  </Button>
                ) : null}
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : null}

          {denormalizedData && tweetId ? (
            <ConversationSummary key={tweetId} tweetId={tweetId} thread={denormalizedData} />
          ) : null}

          {renderThreadSection()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
